import React, { useEffect, useState } from "react";
import * as Icon from "react-feather";
import { Breadcrumb, Button, Card, Divider, Popconfirm, Table, Tag } from "antd";
import { useDispatch } from "react-redux";
import quickLinkService from "../../services/quick-link-service";
import appLoaderAction from "../../redux/action/app-loader-action";
import notifyUtitls from "../../utils/notify-utils";
import fileUtils from "../../utils/file-utils";
import { createdColumnStatic, withCreatedColumn } from "../../shared/functional/created-sort";
import AddEditQuickLinkDialog from "./AddEditQuickLinkDialog";
import { QuickLinkIcon } from "../../constants/quick-link-icons";
import { groupType, VISIBILITY } from "../../constants/app-constants";

/**
 * NMU-608: ทางลัดด่วน — the compact shortcut row above ไฮไลต์รอบรั้ว นมร.
 *
 * A flat list rather than the tabbed shape Intranet Management has: these
 * shortcuts are one row on one page, so there is no section for a record to
 * belong to and nothing for a tab to separate.
 */
const VISIBILITY_LABEL = {
  [VISIBILITY.PUBLIC]: "ทุกคน",
  [VISIBILITY.INTERNAL]: "ภายใน",
  [VISIBILITY.EXTERNAL]: "ภายนอก",
};

const QuickLinkManagement = () => {
  const dispatch = useDispatch();
  const [list, setList] = useState([]);
  const [isShowDialog, setIsShowDialog] = useState(false);
  const [editData, setEditData] = useState(null);

  const doSearch = async () => {
    try {
      dispatch(appLoaderAction.enable());
      setList(await quickLinkService.list());
    } catch (err) {
      notifyUtitls.error(err);
    } finally {
      dispatch(appLoaderAction.disale());
    }
  };

  useEffect(() => {
    doSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDelete = async (id) => {
    try {
      dispatch(appLoaderAction.enable());
      await quickLinkService.remove(id);
      notifyUtitls.success("สำเร็จ", "ลบข้อมูลเรียบร้อย");
      await doSearch();
    } catch (err) {
      notifyUtitls.error(err);
    } finally {
      dispatch(appLoaderAction.disale());
    }
  };

  /**
   * Move a shortcut and persist the resulting order.
   *
   * ลำดับ is a number on each record rather than a list the server stores, so
   * moving one means renumbering the whole list — otherwise two links keep the
   * same seqNo and the row order on the portal is decided by insertion time.
   */
  const onMove = async (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= list.length) return;
    const reordered = [...list];
    const [moved] = reordered.splice(index, 1);
    reordered.splice(target, 0, moved);
    setList(reordered);

    try {
      dispatch(appLoaderAction.enable());
      // Only the two that actually changed places need writing back.
      const lo = Math.min(index, target);
      const hi = Math.max(index, target);
      for (let i = lo; i <= hi; i += 1) {
        const row = reordered[i];
        await quickLinkService.edit(row.quickLinkId, { ...row, seqNo: i + 1 });
      }
    } catch (err) {
      notifyUtitls.error(err);
    } finally {
      dispatch(appLoaderAction.disale());
      await doSearch(); // whatever the server now holds is the truth
    }
  };

  const columns = [
    {
      title: "ลำดับ",
      key: "order",
      width: 130,
      render: (_, row, index) => (
        <div className="d-flex align-items-center">
          <span className="me-2">{row.seqNo || index + 1}</span>
          <Button size="small" className="me-1" disabled={index === 0} onClick={() => onMove(index, -1)}>
            <Icon.ChevronUp size={14} />
          </Button>
          <Button size="small" disabled={index === list.length - 1} onClick={() => onMove(index, 1)}>
            <Icon.ChevronDown size={14} />
          </Button>
        </div>
      ),
    },
    {
      // The glyph or the uploaded emblem — whichever the portal will draw, so
      // the column shows what the visitor gets rather than a key.
      title: "ไอคอน",
      key: "icon",
      width: 110,
      align: "center",
      render: (_, row) =>
        row.attachImage ? (
          <img
            src={fileUtils.getFileRenderUrl(
              groupType.QUICK_LINK,
              row.quickLinkId,
              row.attachImage.originalFilename,
            )}
            alt=""
            style={{ width: 28, height: 28, objectFit: "contain" }}
          />
        ) : (
          <QuickLinkIcon value={row.icon} />
        ),
    },
    {
      title: "ชื่อรายการ",
      key: "title",
      render: (_, row) => (row.title && (row.title.titleTH || row.title.titleEN)) || "-",
    },
    {
      title: "ปลายทาง",
      key: "url",
      render: (_, row) => row.url || "-",
    },
    {
      title: "แท็บใหม่",
      dataIndex: "enableNewTab",
      key: "enableNewTab",
      width: 100,
      align: "center",
      render: (enableNewTab) => (enableNewTab ? <Tag color="blue">เปิด</Tag> : <Tag>ปิด</Tag>),
    },
    {
      title: "การแสดงผล",
      dataIndex: "visibility",
      key: "visibility",
      width: 120,
      render: (visibility) => VISIBILITY_LABEL[visibility] || VISIBILITY_LABEL[VISIBILITY.PUBLIC],
    },
    {
      title: "สถานะ",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (status) => (status ? <Tag color="green">Online</Tag> : <Tag>Offline</Tag>),
    },
    {
      // NMU-600: pinned right, with the scrolling body that makes the columns
      // it covers reachable — the rule every backoffice table follows now.
      title: "ดำเนินการ",
      key: "action",
      width: 130,
      fixed: "right",
      render: (_, row) => (
        <div className="d-flex">
          <label
            className="pe-3 border-end"
            style={{ cursor: "pointer" }}
            onClick={() => {
              setEditData(row);
              setIsShowDialog(true);
            }}
          >
            <Icon.Edit size={18} />
          </label>
          <Popconfirm title="ยืนยันการลบข้อมูล ?" onConfirm={() => onDelete(row.quickLinkId)}>
            <label className="ps-3" style={{ cursor: "pointer" }}>
              <Icon.Trash2 size={18} />
            </label>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div>
      <h2 className="color-primary-title">ทางลัดด่วน</h2>
      <Breadcrumb>
        <Breadcrumb.Item>Content Management</Breadcrumb.Item>
        <Breadcrumb.Item>Quick Links</Breadcrumb.Item>
      </Breadcrumb>
      <Divider className="mt-2 mb-4" />

      <Card bordered={false}>
        <div className="d-flex justify-content-between mb-3">
          <Button
            type="primary"
            onClick={() => {
              setEditData(null);
              setIsShowDialog(true);
            }}
          >
            <Icon.Plus className="me-1" size={18} /> เพิ่มรายการ
          </Button>
        </div>
        <Table
          rowKey={(row) => row.quickLinkId}
          columns={withCreatedColumn(columns, createdColumnStatic)}
          dataSource={list}
          pagination={false}
          scroll={{ x: true }}
        />
      </Card>

      <AddEditQuickLinkDialog
        isShow={isShowDialog}
        editData={editData}
        nextSeqNo={list.length + 1}
        onCancel={() => setIsShowDialog(false)}
        onSaveComplete={() => {
          setIsShowDialog(false);
          doSearch();
        }}
      />
    </div>
  );
};

export default QuickLinkManagement;
